/**
 * The tutorial video list of `/metal-resources/tutorials/`: the featured videos, the newsletter
 * sign-up (`src/components/Shared/StayTuned/StayTuned.tsx`), then the remaining videos. Each item
 * is a `@ryerson/frontend.media` → `Video` player with its Contentful thumbnail, a title and the
 * rich-text description (`src/components/Shared/model/Contentful.tsx` → `renderRichText`, no
 * search highlighting).
 *
 * Its only caller, `src/pages/metal-resources/tutorials/index.tsx`, splits the language's
 * tutorials into the first five (`topVideos`) and the rest (`bottomVideos`). From `lg` up the first
 * featured video is drawn large beside its description and the others as rows; below `lg` every
 * video is a stacked card.
 *
 * Thumbnails are Contentful image URLs with the Images API query (`fm=jpg&fit=fill&w=…&h=…`)
 * appended, sized per slot. The tutorial data shape is
 * `src/contentful/tutorials/types/index.ts` → `ProcessedContentfulTutorialsFields`.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { Video } from "@ryerson/frontend.media";
import { Typography } from "@ryerson/frontend.typography";
import styled from "@emotion/styled";
import { ContentSection } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { VideoWrapperProps } from "@components/Helper/common";
import { getVideoPadding } from "@components/Helper/commonStyles";
import { renderRichText } from "@components/Shared/model/Contentful";
import StayTunedComponent from "@components/Shared/StayTuned/StayTuned";
import { ProcessedContentfulTutorialsFields } from "@contentful/tutorials/types";
import Spacer from "@components/Shared/Spacer/Spacer";
const FirstVideo = styled.div`
	width: 720px;
	display: inline-block;
	height: 396px;
	margin: 80px 0;
	vertical-align: top;
	${(props: VideoWrapperProps) => {
		const { buttonOrientation = "right", buttonSize = "md" } = props;
		return getVideoPadding(buttonOrientation, false, buttonSize);
	}}
`;

const FirstDescription = styled.div`
	width: calc(100% - 720px);
	padding-left: 6.5%;
	display: inline-block;
	vertical-align: top;
	box-sizing: border-box;
`;

const LineItem = styled.div`
	display: block;
	width: 100%;
	padding: 40px 0px;
`;

const VideoContainer = styled.div`
	width: 340px;
	display: inline-block;
	vertical-align: top;
	height: 180px;
	${(props: VideoWrapperProps) => {
		const { buttonOrientation = "right", buttonSize = "sm" } = props;
		return getVideoPadding(buttonOrientation, false, buttonSize);
	}}
`;

const Title = styled.div`
	width: 260px;
	display: inline-block;
	vertical-align: top;
	height: auto;
	margin-left: 60px;
`;

const Description = styled.div`
	width: calc(100% - 780px);
	margin-left: 120px;
	display: inline-block;
	height: auto;
	vertical-align: top;
`;

const Divider = styled.hr`
	width: 100%;
	margin: 0;
	opacity: 0.1;
`;

const MobileVideoContainer = styled.div`
	width: 100%;
	max-width: 340px;
	margin: 0 auto;
`;

const MobileVideoWrapper = styled.div`
	${(props: VideoWrapperProps) => {
		const { buttonOrientation = "top-right", buttonSize = "md" } = props;
		return getVideoPadding(buttonOrientation, true, buttonSize);
	}}
`;

type VideoListProps = {
	/** Shown above the newsletter sign-up; the first is the large featured video on desktop. */
	topVideos: ProcessedContentfulTutorialsFields[];
	/** Shown below the newsletter sign-up. */
	bottomVideos: ProcessedContentfulTutorialsFields[];
};

/** `StayTuned`'s `parent`: the value of its `stay_tuned` GA event for sign-ups made here. */
const stayTunedParent = "Tutorials-VideoList";
/** Renders the featured and remaining tutorial videos around the newsletter sign-up. */
const VideoList: React.FC<VideoListProps> = ({ topVideos, bottomVideos }) => {
	const { theme } = useTheme();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="0">
					{/* The desktop lists key on `contentful_id`, which the tutorial helper sets to
					    "" when the content type has no such field, so the keys can repeat. */}
					{topVideos.map((video: any, index: number) => {
						return (
							<React.Fragment key={video.contentful_id}>
								{index === 0 ? (
									<>
										{/* The browser's URL parser removes the line break
										    and tabs inside this thumbnail URL, so the query
										    still applies. */}
										<FirstVideo>
											<Video
												buttonOrientation="right"
												imageUrl={`${video.videoThumbnail.file.url}
													?fm=jpg&fit=fill&w=655&h=396`}
												videoId={video.videoId}
											/>
										</FirstVideo>
										<FirstDescription>
											<Typography
												css={css`
													letter-spacing: -2px;
													margin-top: 80px;
													margin-bottom: 43px;
												`}
												variant="h2"
											>
												{video.title}
											</Typography>
											<Typography size="md" variant="p">
												{renderRichText(
													video.description.raw,
													"primary",
													null,
													null
												)}
											</Typography>
										</FirstDescription>
									</>
								) : (
									<LineItem>
										<VideoContainer>
											<Video
												buttonSize="sm"
												buttonOrientation="right"
												imageUrl={`${video.videoThumbnail.file.url}?fm=jpg&fit=fill&w=290&h=180`}
												videoId={video.videoId}
											/>
										</VideoContainer>
										<Title>
											<Typography
												variant="h3"
												css={css`
													margin-top: 0px;
												`}
											>
												{video.title}
											</Typography>
										</Title>
										<Description>
											<Typography size="md" variant="p">
												{renderRichText(
													video.description.raw,
													"primary",
													null,
													null
												)}
											</Typography>
										</Description>
									</LineItem>
								)}
								<Divider
									css={css`
										color: ${theme.colors.primary.darkGray};
									`}
								/>
							</React.Fragment>
						);
					})}

					<Spacer
						css={css`
							height: 75px;
						`}
					/>
				</ContentSection>

				<StayTunedComponent parent={stayTunedParent} />

				<ContentSection vPadding="0">
					<Spacer
						css={css`
							height: 75px;
						`}
					/>
					<Divider
						css={css`
							color: ${theme.colors.primary.darkGray};
						`}
					/>
					{bottomVideos.map((video: any, index: number) => {
						return (
							<React.Fragment key={video.contentful_id}>
								<LineItem>
									<VideoContainer>
										<Video
											buttonSize="sm"
											buttonOrientation="right"
											imageUrl={`${video.videoThumbnail.file.url}?fm=jpg&fit=fill&w=290&h=180`}
											videoId={video.videoId}
										/>
									</VideoContainer>
									<Title>
										<Typography
											css={css`
												margin-top: 0px;
											`}
											variant="h3"
										>
											{video.title}
										</Typography>
									</Title>
									<Description>
										<Typography size="md" variant="p">
											{renderRichText(
												video.description.raw,
												"primary",
												null,
												null
											)}
										</Typography>
									</Description>
								</LineItem>
								<Divider
									css={css`
										color: ${theme.colors.primary.darkGray};
									`}
								/>
							</React.Fragment>
						);
					})}
					<Spacer
						css={css`
							height: 280px;
						`}
					/>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection>
					{topVideos.map((video: any, index: number) => {
						return (
							<MobileVideoContainer key={index}>
								<MobileVideoWrapper>
									<Video
										buttonSize="md"
										buttonOrientation="top-right"
										imageUrl={
											video.videoThumbnail.file.url +
											"?fm=jpg&fit=fill&w=340&h=224"
										}
										videoId={video.videoId}
									/>
								</MobileVideoWrapper>
								<Typography
									variant="h3"
									css={css`
										margin-top: 25px;
										margin-bottom: 10px;
									`}
									type="primary"
								>
									{video.title}
								</Typography>
								<Typography type="primary" size="md">
									{renderRichText(video.description.raw, "primary", null, null)}
								</Typography>
							</MobileVideoContainer>
						);
					})}
				</ContentSection>
				<StayTunedComponent parent={stayTunedParent} />
				<ContentSection>
					{bottomVideos.map((video: any, index: number) => {
						return (
							<MobileVideoContainer key={index}>
								<MobileVideoWrapper>
									<Video
										buttonSize="md"
										buttonOrientation="top-right"
										imageUrl={
											video.videoThumbnail.file.url +
											"?fm=jpg&fit=fill&w=340&h=224"
										}
										videoId={video.videoId}
									/>
								</MobileVideoWrapper>
								<Typography
									variant="h3"
									css={css`
										margin-top: 25px;
										margin-bottom: 10px;
									`}
									type="primary"
								>
									{video.title}
								</Typography>
								<Typography type="primary" size="md">
									{renderRichText(video.description.raw, "primary", null, null)}
								</Typography>
							</MobileVideoContainer>
						);
					})}
					<Spacer
						css={css`
							height: 45px;
						`}
					/>
				</ContentSection>
			</Media>
		</>
	);
};

export default VideoList;
